"use client";

import { AlertCircle, CheckCircle2, Info, PlusCircle, X } from "lucide-react";
import { useState } from "react";
import type { ProjectDataReferencePreview } from "@/lib/aicheck/project-data-diff";
import type { ProjectDataComparisonResult } from "./SourceWorkspace";

interface ReferencePopupState {
  label: string;
  value: string;
  path: string;
  reference?: ProjectDataReferencePreview;
}

function severityClass(severity: string): string {
  if (severity === "error") return "border-red-200 bg-red-50 text-red-700";
  if (severity === "warning") {
    return "border-yellow-200 bg-yellow-50 text-yellow-700";
  }
  return "border-blue-200 bg-blue-50 text-blue-700";
}

function kindLabel(kind: string): string {
  if (kind === "missing_in_candidate") return "Missing";
  if (kind === "extra_in_candidate") return "Additional";
  if (kind === "conflict") return "Conflict";
  return kind;
}

function originLabel(origin: string | undefined): string | null {
  if (!origin) return null;
  if (origin === "base_existing") return "Existing in Base";
  if (origin === "candidate_introduced") return "Introduced by Candidate";
  if (origin === "merge_induced") return "Merge-induced";
  if (origin === "base_only") return "Base only";
  if (origin === "candidate_only") return "Candidate only";
  return "Unknown origin";
}

function originClass(origin: string | undefined): string {
  if (origin === "base_existing")
    return "border-gray-200 bg-gray-50 text-gray-700";
  if (origin === "candidate_introduced") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (origin === "merge_induced")
    return "border-red-200 bg-red-50 text-red-700";
  if (origin === "candidate_only") {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  return "border-gray-200 bg-white text-gray-600";
}

function formatValue(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

function fallbackReference(
  reference: ReferencePopupState,
): ProjectDataReferencePreview {
  return {
    title: reference.value || reference.path,
    fields: [{ label: "Displayed value", value: reference.value }],
    raw: reference.value,
  };
}

function formatRawData(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function ComparisonView({
  result,
}: {
  result: ProjectDataComparisonResult;
}) {
  const [selectedReference, setSelectedReference] =
    useState<ReferencePopupState | null>(null);
  const hasFindings = result.findings.length > 0;
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-4">
        <SummaryBox
          label="Matches"
          value={result.summary.matches}
          className="border-green-200 bg-green-50 text-green-700"
        />
        <SummaryBox
          label="Conflicts"
          value={result.summary.conflicts}
          className="border-red-200 bg-red-50 text-red-700"
        />
        <SummaryBox
          label="Missing"
          value={result.summary.missingInCandidate}
          className="border-yellow-200 bg-yellow-50 text-yellow-700"
        />
        <SummaryBox
          label="Additional"
          value={result.summary.extraInCandidate}
          className="border-blue-200 bg-blue-50 text-blue-700"
        />
      </div>

      {(result.summary.finalValidationFindings ?? 0) > 0 && (
        <div className="grid gap-3 md:grid-cols-4">
          <SummaryBox
            label="Final itinerary issues"
            value={result.summary.finalValidationFindings ?? 0}
            className="border-red-200 bg-red-50 text-red-700"
          />
          <SummaryBox
            label="Existing in Base"
            value={result.summary.baseExisting ?? 0}
            className="border-gray-200 bg-gray-50 text-gray-700"
          />
          <SummaryBox
            label="Introduced by Candidate"
            value={result.summary.candidateIntroduced ?? 0}
            className="border-amber-200 bg-amber-50 text-amber-700"
          />
          <SummaryBox
            label="Merge-induced"
            value={result.summary.mergeInduced ?? 0}
            className="border-red-200 bg-red-50 text-red-700"
          />
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <div className="grid gap-3 md:grid-cols-2">
          <SourceLabel label="Base" value={result.baseSource.label} />
          <SourceLabel label="Candidate" value={result.candidateSource.label} />
        </div>
      </div>

      {!hasFindings && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
          <CheckCircle2 size={16} />
          No deterministic differences were found.
        </div>
      )}

      {hasFindings && (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="border-b border-gray-100 bg-white px-4 py-3">
            <h3 className="text-sm font-bold text-gray-900">
              ProjectData Differences
            </h3>
          </div>
          <div className="divide-y divide-gray-100 bg-white">
            {result.findings.map((finding) => (
              <div key={finding.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex h-6 items-center gap-1 rounded-md border px-2 text-xs font-semibold ${severityClass(
                          finding.severity,
                        )}`}
                      >
                        {finding.kind === "conflict" ? (
                          <AlertCircle size={13} />
                        ) : finding.kind === "extra_in_candidate" ? (
                          <PlusCircle size={13} />
                        ) : (
                          <Info size={13} />
                        )}
                        {kindLabel(finding.kind)}
                      </span>
                      {originLabel(finding.origin) && (
                        <span
                          className={`inline-flex h-6 items-center rounded-md border px-2 text-xs font-semibold ${originClass(
                            finding.origin,
                          )}`}
                        >
                          {originLabel(finding.origin)}
                        </span>
                      )}
                      {finding.scope === "final-validation" && (
                        <span className="inline-flex h-6 items-center rounded-md border border-purple-200 bg-purple-50 px-2 text-xs font-semibold text-purple-700">
                          Final itinerary
                        </span>
                      )}
                      <span className="text-sm font-bold text-gray-900">
                        {finding.title}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      {finding.message}
                    </p>
                  </div>
                  {typeof finding.matchConfidence === "number" && (
                    <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
                      {(finding.matchConfidence * 100).toFixed(0)}% match
                    </span>
                  )}
                </div>

                {(finding.baseValue != null ||
                  finding.candidateValue != null ||
                  finding.basePath ||
                  finding.candidatePath) && (
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <EvidenceBox
                      label="Base"
                      value={formatValue(finding.baseValue)}
                      path={finding.basePath}
                      reference={finding.baseReference}
                      onOpenReference={setSelectedReference}
                    />
                    <EvidenceBox
                      label="Candidate"
                      value={formatValue(finding.candidateValue)}
                      path={finding.candidatePath}
                      reference={finding.candidateReference}
                      onOpenReference={setSelectedReference}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {selectedReference && (
        <ReferencePopup
          reference={selectedReference}
          onClose={() => setSelectedReference(null)}
        />
      )}
    </div>
  );
}

function SummaryBox({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className: string;
}) {
  return (
    <div className={`rounded-lg border p-3 ${className}`}>
      <div className="text-xl font-bold">{value}</div>
      <div className="mt-1 text-xs font-medium">{label}</div>
    </div>
  );
}

function SourceLabel({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold text-gray-900">
        {value}
      </div>
    </div>
  );
}

function EvidenceBox({
  label,
  value,
  path,
  reference,
  onOpenReference,
}: {
  label: string;
  value: string;
  path?: string;
  reference?: ProjectDataReferencePreview;
  onOpenReference: (reference: ReferencePopupState) => void;
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
      <div className="text-xs font-semibold text-gray-500">{label}</div>
      <div className="mt-1 break-words text-sm text-gray-900">{value}</div>
      {path && (
        <button
          type="button"
          onClick={() => onOpenReference({ label, value, path, reference })}
          className="mt-1 block break-all text-left font-mono text-xs text-blue-600 transition-colors hover:text-blue-800 hover:underline"
        >
          {path}
        </button>
      )}
    </div>
  );
}

function ReferencePopup({
  reference,
  onClose,
}: {
  reference: ReferencePopupState;
  onClose: () => void;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const preview = reference.reference ?? fallbackReference(reference);
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close data preview"
        className="absolute inset-0 bg-zinc-950/35"
        onClick={onClose}
      />
      <div className="relative flex max-h-[min(82vh,760px)] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-500">
              {reference.label}
            </div>
            <div className="mt-1 truncate text-sm font-bold text-gray-900">
              {preview.title}
            </div>
            <div className="mt-1 break-all font-mono text-xs text-gray-500">
              {reference.path}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label={showRaw ? "Show summary" : "Show raw data"}
              aria-pressed={showRaw}
              title={showRaw ? "Show summary" : "Show raw data"}
              onClick={() => setShowRaw((current) => !current)}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border text-gray-600 transition-colors hover:bg-gray-50 ${
                showRaw
                  ? "border-blue-200 bg-blue-50 text-blue-700"
                  : "border-gray-200 bg-white"
              }`}
            >
              <Info size={15} />
            </button>
            <button
              type="button"
              aria-label="Close data preview"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto px-4 py-3">
          {showRaw ? (
            <pre className="max-h-[60vh] overflow-auto rounded-md bg-gray-950 p-3 text-xs leading-relaxed text-gray-100">
              {formatRawData(preview.raw)}
            </pre>
          ) : (
            <div className="space-y-3">
              {preview.subtitle && (
                <div className="text-sm font-medium text-gray-700">
                  {preview.subtitle}
                </div>
              )}
              <dl className="grid gap-2 sm:grid-cols-2">
                {preview.fields.map((field) => (
                  <div
                    key={`${field.label}:${field.value}`}
                    className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
                  >
                    <dt className="text-xs font-semibold text-gray-500">
                      {field.label}
                    </dt>
                    <dd className="mt-1 break-words text-sm text-gray-900">
                      {field.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
